<template>
  <div class="h-full flex flex-col bg-[#f8fafc] overflow-hidden min-w-0">
    <!-- Item Modal -->
    <ItemModal
      :isOpen="showModal"
      :editItem="editingItem"
      @close="closeModal"
      @save="handleSave"
      @update="handleUpdate"
    />
    
    <!-- Controls Section -->
    <div class="grid grid-cols-2 md:grid-cols-4 items-center mb-4 gap-3 shrink-0 min-w-0 w-full overflow-x-hidden">
      <select
        v-model="selectedCategoryId"
        class="w-full min-w-0 px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
      >
        <option value="">By Category (All)</option>
        <option v-for="cat in categories" :key="cat.id" :value="String(cat.id)">
          {{ cat.name }}
        </option>
      </select>
      <button
        type="button"
        @click="handleImport"
        :disabled="!canImportItems"
        class="w-full min-w-0 px-3 py-2 text-[0.75rem] sm:text-[0.8rem] font-bold rounded-md transition-all flex items-center justify-center gap-1.5"
        :class="canImportItems ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-60'"
      >
        <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
        Import (XSL)
      </button>
      <button
        type="button"
        @click="downloadSample"
        class="w-full min-w-0 px-3 py-2 bg-blue-50 text-blue-700 text-[0.75rem] sm:text-[0.8rem] font-bold rounded-md hover:bg-blue-100 transition-all flex items-center justify-center gap-1.5 border border-blue-100"
      >
        <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
        Sample Form
      </button>
      <input
        v-model="searchQuery"
        type="text"
        class="w-full min-w-0 px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white md:max-w-[220px] md:justify-self-end"
        placeholder="Search by code or name..."
      >
    </div>

    <!-- Table: Following users list style -->
    <div class="w-full flex-1 min-h-0 overflow-x-auto overflow-y-auto">
    <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
      <thead>
        <tr>
          <th>No.</th>
          <th @click="sortBy('itemCode')" class="sort-col">
            Item Code <span v-if="sortKey === 'itemCode'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('itemName')" class="sort-col">
            Item Name <span v-if="sortKey === 'itemName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('category')" class="sort-col">
            Category <span v-if="sortKey === 'category'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('unit')" class="sort-col">
            Unit <span v-if="sortKey === 'unit'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('openingStock')" class="sort-col">
            Min Level <span v-if="sortKey === 'openingStock'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th @click="sortBy('current_balance')" class="sort-col">
            Current Balance <span v-if="sortKey === 'current_balance'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
           <th @click="sortBy('recentPrice')" class="sort-col">
            Recent Price <span v-if="sortKey === 'recentPrice'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
          </th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in filteredItems" :key="item.key" class="hover:bg-slate-100/50 transition-colors border-b">
          <td>{{ index + 1 }}</td>
          <td>{{ item.itemCode }}</td>
          <td :style="item.rowType === 'product' ? 'color:#16a34a; font-weight:600;' : ''">{{ item.itemName }}</td>
          <td>{{ item.categoryLabel }}</td>
          <td>{{ item.unit }}</td>
          <td>{{ item.minLevel }}</td>
          <td>
            <span class="px-2 py-0.5 rounded text-[11px] font-bold border"
                  :class="item.current_balance > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'">
              {{ formatBalanceQty(item.current_balance) }}
            </span>
          </td>
          <td>{{ (item.recentPrice || 0).toFixed(2) }}</td>

          <td class="actions">
            <button
              v-if="item.rowType !== 'product'"
              @click="editItem(item.raw)"
              class="edit-btn"
              :class="canEditItemRegister ? 'hover:underline' : 'opacity-50 cursor-not-allowed action-disabled-marker'"
              :disabled="!canEditItemRegister"
            >Edit</button>
            <button v-else class="edit-btn" style="opacity:0.4; cursor:not-allowed; text-decoration:none;" disabled>Edit</button>
            <label class="toggle-switch" :class="{ 'action-disabled-marker': !canEditItemRegister }">
              <input type="checkbox" :checked="item.status === 1" :disabled="!canEditItemRegister" @change="toggleItemStatus(item)">
              <span class="slider"></span>
            </label>
            <button
              v-if="isItemScannable(item)"
              type="button"
              class="barcode-action-btn"
              title="Generate barcodes"
              :aria-label="`Generate barcodes for ${item.itemName}`"
              @click="openBarcodeModal(item)"
            >
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-width="1.6" d="M3 5v14M6 5v14M9 5v14M13 5v14M16 5v14M18 5v14M21 5v14" />
              </svg>
            </button>
          </td>
        </tr>
        <tr v-if="loading">
          <td colspan="9" class="text-center py-10">
            <div class="flex items-center justify-center">
              <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </td>
        </tr>
        <tr v-else-if="filteredItems.length === 0">
          <td colspan="9" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
            No items available.
          </td>
        </tr>
      </tbody>
    </table>
    </div>
    <div v-if="showBarcodeModal" class="fixed inset-0 z-[250] flex items-center justify-center bg-black/50 p-4">
      <div class="w-full max-w-sm rounded-xl bg-white shadow-2xl">
        <div class="flex items-center justify-between rounded-t-xl bg-[#0d4169] px-5 py-3 text-white">
          <h3 class="text-base font-bold">Generate Barcodes</h3>
          <button type="button" class="rounded px-2 py-1 text-lg hover:bg-white/10" aria-label="Close" @click="closeBarcodeModal">×</button>
        </div>
        <form class="space-y-4 p-5" @submit.prevent="generateItemBarcodes">
          <p class="text-sm text-slate-600">{{ barcodeItem?.itemName }}</p>
          <div class="flex flex-col gap-1.5">
            <label for="barcode-count" class="text-sm font-semibold text-slate-700">How many bar codes</label>
            <input
              id="barcode-count"
              v-model.number="barcodeCount"
              type="number"
              min="1"
              step="1"
              inputmode="numeric"
              required
              class="rounded-md border border-[#cbd5e1] px-3 py-2 text-sm outline-none focus:border-[#0d4169]"
              @input="barcodeError = ''"
            >
            <p v-if="barcodeError" class="text-xs font-semibold text-red-600">{{ barcodeError }}</p>
          </div>
          <div class="flex justify-end gap-2">
            <button type="button" class="rounded-md bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-300" @click="closeBarcodeModal">Cancel</button>
            <button type="submit" class="rounded-md bg-[#0d4169] px-4 py-2 text-sm font-bold text-white hover:bg-[#0a3354]">Generate Barcodes</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { jsPDF } from 'jspdf'
import JsBarcode from 'jsbarcode'
import ItemModal from './ItemModal.vue'
import { apiFetch, WS_BASE_URL } from '../../utils/api'
import store from '../../store'

const items = ref([])
const products = ref([])
const categories = ref([])
const selectedCategoryId = ref('')
const searchQuery = ref('')
const sortKey = ref('itemName')
const sortOrder = ref('asc')
const showModal = ref(false)
const editingItem = ref(null)
const loading = ref(false)
const showBarcodeModal = ref(false)
const barcodeItem = ref(null)
const barcodeCount = ref(1)
const barcodeError = ref('')

let itemSyncSocket = null
let itemSyncReconnectTimer = null

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch?.id || null)
const effectiveBranchId = computed(() => {
  const user = currentUser.value
  if (!user) return null
  const rawBranchId = user.branchId ?? user.branch_id ?? user.branch?.id ?? null
  if (rawBranchId == null || rawBranchId === '') return null
  const parsed = Number(rawBranchId)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
})
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() =>
  userPrivileges.value?.All === true || userPrivileges.value?.all === true
)
function getPrivilegeBlock(name) {
  return userPrivileges.value?.[name] || userPrivileges.value?.[String(name).toLowerCase()] || null
}
const itemRegisterPrivilege = computed(() =>
  getPrivilegeBlock('Item Register')
)
const canEditItemRegister = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!itemRegisterPrivilege.value?.Edit
})
const canImportItems = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!itemRegisterPrivilege.value?.Add
})

function normalizeUnit(unit) {
  return (unit || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

function isLiterOrKgUnit(unit) {
  const unitNorm = normalizeUnit(unit)
  return (
    unitNorm === 'l' ||
    unitNorm === 'liter' ||
    unitNorm === 'liters' ||
    unitNorm.startsWith('liter') ||
    unitNorm === 'kg' ||
    unitNorm === 'kilogram'
  )
}

function formatBalanceQty(value) {
  const n = typeof value === 'number' ? value : parseFloat(value ?? 0)
  if (!Number.isFinite(n)) return '0.000'
  return (Math.round(n * 1000) / 1000).toFixed(3)
}

function getDerivedProductRecentPrice(unit, recentUnitPrice, recentVolume) {
  const price = Number(recentUnitPrice || 0)
  const volume = Number(recentVolume || 0)
  if (!(volume > 0)) return 0

  const perVolume = price / volume
  return isLiterOrKgUnit(unit) ? perVolume * 1000 : perVolume
}

function getDerivedItemRecentPrice(unit, recentUnitPrice, recentVolume) {
  const price = Number(recentUnitPrice || 0)
  if (!isLiterOrKgUnit(unit)) return price

  const volume = Number(recentVolume || 0)
  if (!(volume > 0)) return 0

  return (price / volume) * 1000
}

async function loadItems() {
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)
    
    const response = await apiFetch(`/items?${params.toString()}`)
    if (response.ok) {
      const data = await response.json()
      // Attach derived recent price per item:
      // liter/K.G. => (recent_unit_price / recent_volume) * 1000
      // others     => recent_unit_price
      items.value = (data || []).map((item) => {
        const derived = getDerivedItemRecentPrice(
          item.unit,
          item.recent_unit_price,
          item.recent_volume
        )
        return { ...item, derivedRecentPrice: derived }
      })
    }
  } catch (error) {
    console.error('Error loading items:', error)
  }
}

async function loadProducts() {
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)

    const response = await apiFetch(`/products?${params.toString()}`)
    if (response.ok) {
      const data = await response.json()
      products.value = (data || []).map((p) => {
        const unit = p.uom || p.unit || ''
        const recentUnitPrice = p.recent_unit_price ?? p.recentUnitPrice ?? 0
        const recentVolume = p.recent_volume ?? p.recentVolume ?? 0
        const derived = getDerivedProductRecentPrice(unit, recentUnitPrice, recentVolume)
        return {
          ...p,
          recent_unit_price: recentUnitPrice,
          recent_volume: recentVolume,
          derivedRecentPrice: derived,
        }
      })
    }
  } catch (error) {
    console.error('Error loading products:', error)
  }
}

async function loadCategories() {
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)
    const response = await apiFetch(`/categories?${params.toString()}`)
    if (response.ok) {
      const data = await response.json().catch(() => [])
      categories.value = Array.isArray(data) ? data : []
    }
  } catch (error) {
    console.error('Error loading categories:', error)
  }
}

// Recalculate Current Balance using the same stock-balance endpoints used elsewhere
// - Items: /items/:id/stock-balance
// - Products: /products/:id/stock-balance (same logic as adjustStock.vue: sum row.balance)
async function loadCurrentBalances() {
  const params = new URLSearchParams()
  if (branchId.value) params.append('branchId', branchId.value)
  else if (companyId.value) params.append('companyId', companyId.value)
  const query = params.toString()

  try {
    const [itemRes, productRes] = await Promise.all([
      apiFetch(`/items/stock-balance-summary?${query}`),
      apiFetch(`/products/stock-balance-summary?${query}`)
    ])

    const itemSummary = itemRes.ok ? await itemRes.json().catch(() => []) : []
    const productSummary = productRes.ok ? await productRes.json().catch(() => []) : []
    const itemMap = new Map((Array.isArray(itemSummary) ? itemSummary : []).map(r => [Number(r.id), Number(r.current_balance || 0)]))
    const productMap = new Map((Array.isArray(productSummary) ? productSummary : []).map(r => [Number(r.id), Number(r.current_balance || 0)]))

    ;(items.value || []).forEach((item) => {
      const raw = Number(itemMap.get(Number(item.id)) || 0)
      // Never show negative Current Balance (float / liter-kg conversion rounding dust)
      item.current_balance = Math.max(0, Math.round(raw * 1000) / 1000)
    })
    ;(products.value || []).forEach((p) => {
      const raw = Number(productMap.get(Number(p.id)) || 0)
      p.current_balance = Math.max(0, Math.round(raw * 1000) / 1000)
    })
  } catch (err) {
    console.error('Failed to load stock balance summaries', err)
    ;(items.value || []).forEach((item) => { item.current_balance = 0 })
    ;(products.value || []).forEach((p) => { p.current_balance = 0 })
  }
}

const mergedRows = computed(() => {
  const itemRows = (items.value || []).map((i) => ({
    rowType: 'item',
    id: i.id,
    key: `item:${i.id}`,
    itemCode: i.itemCode,
    itemName: i.itemName,
    categoryLabel: i.category?.name || 'N/A',
    unit: i.unit,
    minLevel: i.minStock,
    current_balance: Number(i.current_balance || 0),
    recentPrice: Number(i.derivedRecentPrice || 0),
    status: i.status,
    raw: i,
  }))

  const productRows = (products.value || []).map((p) => ({
    rowType: 'product',
    id: p.id,
    key: `product:${p.id}`,
    // Per request: show literal "Product" in Item Code column
    itemCode: 'Product',
    itemName: p.name,
    categoryLabel: '-',
    unit: p.uom || p.unit || '',
    minLevel: '-',
    current_balance: Number(p.current_balance || 0),
    // Per request: recent_unit_price from the product itself
    recentPrice: Number(p.derivedRecentPrice || 0),
    status: p.status,
    raw: p,
  }))

  return [...itemRows, ...productRows]
})

const filteredItems = computed(() => {
  let result = [...mergedRows.value]

  if (selectedCategoryId.value) {
    // Requirement: when category selected show items on that category
    result = result.filter(row => {
      if (row.rowType !== 'item') return false
      const catId = row.raw?.categoryId ?? row.raw?.category_id ?? row.raw?.category?.id ?? null
      return String(catId || '') === String(selectedCategoryId.value)
    })
  }

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(row => 
      String(row.itemName || '').toLowerCase().includes(query) ||
      String(row.itemCode || '').toLowerCase().includes(query)
    )
  }

  result.sort((a, b) => {
    // Keep inactive (status off) at the bottom without changing column sort among peers
    const aOff = Number(a.status) === 1 ? 0 : 1
    const bOff = Number(b.status) === 1 ? 0 : 1
    if (aOff !== bOff) return aOff - bOff

    const modifier = sortOrder.value === 'asc' ? 1 : -1

    // Special handling for numeric fields
    if (sortKey.value === 'current_balance' || sortKey.value === 'recentPrice') {
      const aVal = sortKey.value === 'recentPrice' ? Number(a.recentPrice || 0) : Number(a.current_balance || 0)
      const bVal = sortKey.value === 'recentPrice' ? Number(b.recentPrice || 0) : Number(b.current_balance || 0)
      if (aVal < bVal) return -1 * modifier
      if (aVal > bVal) return 1 * modifier
      return 0
    }

    let valA = ''
    let valB = ''
    if (sortKey.value === 'category') {
      valA = String(a.categoryLabel || '').toLowerCase()
      valB = String(b.categoryLabel || '').toLowerCase()
    } else {
      valA = String(a[sortKey.value] || '').toLowerCase()
      valB = String(b[sortKey.value] || '').toLowerCase()
    }
    if (valA < valB) return -1 * modifier
    if (valA > valB) return 1 * modifier
    return 0
  })

  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function isItemScannable(item) {
  const value = item?.raw?.is_scannable ?? item?.raw?.isScannable
  return item?.rowType === 'item' && (value === true || Number(value) === 1)
}

function openBarcodeModal(item) {
  if (!isItemScannable(item) || !Number.isInteger(Number(item.id)) || Number(item.id) <= 0) return
  barcodeItem.value = item
  barcodeCount.value = 1
  barcodeError.value = ''
  showBarcodeModal.value = true
}

function closeBarcodeModal() {
  showBarcodeModal.value = false
  barcodeItem.value = null
  barcodeError.value = ''
}

function makeBarcodeValue(companyId, branchId, itemId, usedValues) {
  let serial
  do {
    const randomPart = String(Math.floor(100000 + Math.random() * 900000))
    serial = `${companyId}A${branchId}A${itemId}A${randomPart}`
  } while (usedValues.has(serial))
  usedValues.add(serial)
  return serial
}

function generateItemBarcodes() {
  const count = Number(barcodeCount.value)
  const companyIdValue = Number(companyId.value)
  const branchIdValue = effectiveBranchId.value || 0
  const itemId = Number(barcodeItem.value?.id)

  if (!Number.isInteger(count) || count <= 0) {
    barcodeError.value = 'Enter a whole number greater than 0.'
    return
  }

  if (!Number.isInteger(itemId) || itemId <= 0) {
    barcodeError.value = 'The selected item has an invalid ID.'
    return
  }

  if (!Number.isInteger(companyIdValue) || companyIdValue <= 0) {
    barcodeError.value = 'The current company has an invalid ID.'
    return
  }

  try {
    const generatedValues = new Set()
    const generatedBarcodes = Array.from({ length: count }, () =>
      makeBarcodeValue(companyIdValue, branchIdValue, itemId, generatedValues)
    )
    createBarcodePdf(generatedBarcodes)
    barcodeError.value = ''
    closeBarcodeModal()
  } catch (error) {
    console.error('Failed to generate item barcodes:', error)
    barcodeError.value = error.message || 'Could not generate the barcode PDF. Please try again.'
  }
}

function createBarcodePdf(values) {
  const doc = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true,
    precision: 4,
  })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const maxColumns = 3
  const marginX = 5
  const marginY = 5
  const columnGap = 3
  const rowGap = 7
  const barcodeHeight = 14
  const rowsPerPage = Math.floor(
    (pageHeight - marginY * 2 + rowGap) / (barcodeHeight + rowGap)
  )
  const minModuleWidth = 0.5

  const encodings = values.map((value) => {
    const barcode = JsBarcode({}, value, {
      format: 'CODE128',
      width: 2,
      height: 1,
      displayValue: false,
      margin: 24,
    })
    const encoding = barcode._encodings[0]?.[0]
    if (!encoding?.data || !encoding.options) {
      throw new Error('Code 128 barcode could not be encoded.')
    }
    const { options, data } = encoding
    const quietModules = (options.marginLeft + options.marginRight) / options.width
    return { data, quietModules, totalModules: data.length + quietModules }
  })

  const maxModules = encodings.reduce(
    (maximum, encoding) => Math.max(maximum, encoding.totalModules),
    0
  )
  const usableWidth = pageWidth - marginX * 2
  const columnsThatFit = Math.floor(
    (usableWidth + columnGap) /
    (maxModules * minModuleWidth + 2 + columnGap)
  )
  if (columnsThatFit < 1) {
    throw new Error('Barcode value is too long to print at a reliably scannable size.')
  }

  const columns = Math.min(maxColumns, columnsThatFit)
  const cellWidth = (usableWidth - columnGap * (columns - 1)) / columns
  const cellHeight =
    (pageHeight - marginY * 2 - rowGap * (rowsPerPage - 1)) /
    rowsPerPage
  const labelsPerPage = columns * rowsPerPage

  encodings.forEach((encoding, index) => {
    if (index > 0 && index % labelsPerPage === 0) doc.addPage()
    const indexOnPage = index % labelsPerPage
    const column = indexOnPage % columns
    const row = Math.floor(indexOnPage / columns)
    const cellX = marginX + column * (cellWidth + columnGap)
    const cellY = marginY + row * (cellHeight + rowGap)
    const barcodeWidth = Math.min(cellWidth - 2, encoding.totalModules * minModuleWidth)
    const moduleWidth = barcodeWidth / encoding.totalModules
    const left = cellX + (cellWidth - barcodeWidth) / 2 +
      (encoding.quietModules / 2) * moduleWidth
    const top = cellY + (cellHeight - barcodeHeight) / 2

    doc.setFillColor(0, 0, 0)
    let barStart = -1
    for (let moduleIndex = 0; moduleIndex <= encoding.data.length; moduleIndex++) {
      const isBar = encoding.data[moduleIndex] === '1'
      if (isBar && barStart < 0) barStart = moduleIndex
      if (!isBar && barStart >= 0) {
        doc.rect(
          left + barStart * moduleWidth,
          top,
          (moduleIndex - barStart) * moduleWidth,
          barcodeHeight,
          'F'
        )
        barStart = -1
      }
    }
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.setTextColor(0, 0, 0)
    doc.text(
      String(barcodeItem.value?.itemName || 'Item'),
      cellX + cellWidth / 2,
      top + barcodeHeight + 2.5,
      { align: 'center', maxWidth: cellWidth - 2 }
    )
  })

  const safeItemName = String(barcodeItem.value?.itemName || 'Item')
    .replace(/[\\/:*?"<>|]/g, '-')
    .trim()
  const currentDate = new Date()
  const dateStamp = [
    currentDate.getFullYear(),
    String(currentDate.getMonth() + 1).padStart(2, '0'),
    String(currentDate.getDate()).padStart(2, '0'),
  ].join('-')
  doc.save(`${safeItemName}-${dateStamp}-barcodes.pdf`)
}

function openAddModal() {
  editingItem.value = null
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editingItem.value = null
}

function editItem(item) {
  if (!canEditItemRegister.value) return
  editingItem.value = { ...item } // Clone to avoid direct mutation
  showModal.value = true
}

function handleSave() {
  loadItems()
}

function handleUpdate() {
  loadItems()
}

function downloadSample() {
  const link = document.createElement('a')
  link.href = '/item_sample.xlsx'
  link.setAttribute('download', 'item_sample.xlsx')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function handleImport() {
  if (!canImportItems.value) return
  if (!selectedCategoryId.value) {
    alert('Please select a category before importing.')
    return
  }
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.xlsx, .csv'
  input.onchange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    if (branchId.value != null && String(branchId.value).trim() !== '') {
      formData.append('branchId', String(branchId.value))
    } else if (companyId.value != null && String(companyId.value).trim() !== '') {
      formData.append('companyId', String(companyId.value))
    }
    formData.append('created_by', String(currentUser.value.id))
    formData.append('categoryId', selectedCategoryId.value)

    try {
      const response = await apiFetch('/items/import-excel', {
        method: 'POST',
        headers: {},
        body: formData,
      })

      const result = await response.json()
      if (response.ok) {
        const importErrors = Array.isArray(result?.errors) ? result.errors : []
        if (importErrors.length > 0) {
          const preview = importErrors.slice(0, 8).join('\n')
          const more = importErrors.length > 8 ? `\n...and ${importErrors.length - 8} more error(s).` : ''
          alert(`${result.message}\n\nSome rows were rejected (including duplicate Item Code/Item Name in same Company + Branch scope):\n${preview}${more}`)
        } else {
          alert(result.message)
        }
        await reloadAll()
      } else {
        const importErrors = Array.isArray(result?.errors) ? result.errors : []
        if (importErrors.length > 0) {
          const preview = importErrors.slice(0, 8).join('\n')
          const more = importErrors.length > 8 ? `\n...and ${importErrors.length - 8} more error(s).` : ''
          alert(`${result.message || 'Error importing items'}\n\n${preview}${more}`)
          console.error('Import errors:', importErrors)
        } else {
          alert(result.message || 'Error importing items')
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      alert('Network error')
    }
  }
  input.click()
}

async function toggleItemStatus(item) {
  if (!canEditItemRegister.value) return
  try {
    const newStatus = item.status === 1 ? 0 : 1
    const endpoint = item.rowType === 'product' ? `/products/${item.id}` : `/items/${item.id}`
    const response = await apiFetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify({ 
        status: newStatus,
        updated_by: currentUser.value.id
      })
    })
    if (response.ok) {
      await reloadAll()
    }
  } catch (error) {
    console.error('Error toggling status:', error)
  }
}

async function reloadAll() {
  loading.value = true
  try {
    await Promise.all([loadItems(), loadProducts(), loadCategories()])
    await loadCurrentBalances()
  } finally {
    loading.value = false
  }
}

function disconnectItemSyncRealtime() {
  if (itemSyncReconnectTimer) {
    clearTimeout(itemSyncReconnectTimer)
    itemSyncReconnectTimer = null
  }
  if (itemSyncSocket) {
    try {
      itemSyncSocket.close()
    } catch (_) {
      // ignore close failures
    }
    itemSyncSocket = null
  }
}

function connectItemSyncRealtime() {
  disconnectItemSyncRealtime()

  const user = currentUser.value
  if (!user || typeof WebSocket === 'undefined') return

  const scopedCompanyId = user.companyId || user.company_id || user.company?.id
  if (!scopedCompanyId) return

  const scopedBranchId = user.branchId || user.branch_id || user.branch?.id || null
  const socket = new WebSocket(WS_BASE_URL)
  itemSyncSocket = socket

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({
      type: 'frontend.subscribe',
      company_id: scopedCompanyId,
      branch_id: scopedBranchId,
    }))
  })

  socket.addEventListener('message', (event) => {
    try {
      const message = JSON.parse(event.data)
      if (message.event?.startsWith('item.')) {
        reloadAll()
      }
    } catch (_) {
      // ignore malformed realtime payloads
    }
  })

  socket.addEventListener('close', () => {
    itemSyncSocket = null
    itemSyncReconnectTimer = setTimeout(() => {
      connectItemSyncRealtime()
    }, 3000)
  })
}

onMounted(() => {
  reloadAll()
  connectItemSyncRealtime()
  window.addEventListener('open-item-modal', openAddModal)
})

onUnmounted(() => {
  disconnectItemSyncRealtime()
  window.removeEventListener('open-item-modal', openAddModal)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
}

th, td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

th.sort-col {
  cursor: pointer;
}
th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f1f5f9;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.actions .barcode-action-btn {
  width: 26px;
  height: 26px;
  justify-content: center;
  border-radius: 4px;
  color: #0d4169;
  text-decoration: none;
}

.actions .barcode-action-btn:hover {
  background: #e0f2fe;
}

.action-disabled-marker {
  position: relative;
}

.action-disabled-marker:hover::after {
  content: '⊘';
  position: absolute;
  top: -8px;
  right: -10px;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  line-height: 1;
  pointer-events: none;
}

.status-pill {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: bold;
}
.active  { background: #dcfce7; color: #166534; }
.inactive { background: #fee2e2; color: #991b1b; }

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}
.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
.toggle-switch input:checked + .slider {
  background: #22c55e;
}
.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}
.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}
</style>